import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Portal, svgSprite, zendoStorage } from '../../../hybrid/wrappers';
import T from '../../../common/components/T';
import { LangContext } from '../../../common/decorators/Language';
import UserContext from '../../../common/utils/getContext';
import { getFormData } from '../initData';
import FormComponent from '../../form/FormComponent/FormComponent';
import { getWidgetTranslates } from '../../../common/utils/utils';
import { getGeoName } from '../../../common/utils/memo';
import Skeleton from '../../plug/Skeleton/Skeleton';
import css from './RegionAndCurrencySwitcherModal.module.scss';
import { useRouter } from 'next/router';

const RenderForm = ({ response, setIsShowPortalModal, setIsShowConfirmModal, cancel, isStorePage }) => {
    const { geoname, trans } = response;
    const { localization, lang, activeLangs, setState, currencies, settings, userToken, display_country } =
        useContext(UserContext || {}) || {};
    const langsForMenu = useMemo(() => activeLangs?.filter((l) => l.active && l.show), [activeLangs]);
    const activeLanguage = useMemo(() => {
        return (
            (activeLangs && activeLangs?.find((language) => language.alias === localization.language)) ||
            activeLangs?.find((language) => language.alias === lang) ||
            activeLangs?.find((language) => language.isDefault) ||
            null
        );
    }, [activeLangs, lang, localization?.language]);
    const [formData, setFormData] = useState({
        country: {
            value: geoname?.geoname_id,
            label: geoname?.name,
            code: geoname?.country
        },
        language: {
            value: activeLanguage?.alias,
            label: activeLanguage?.name
        }
    });
    const router = useRouter();
    useEffect(() => {
        (async () => {
            if (localization) {
                const { id, value } = localization?.country || {};
                if (value || id) {
                    const geonameData = await getGeoName(id || value, lang);
                    setFormData((pre) => {
                        return {
                            ...pre,
                            country: {
                                value: geonameData?.data.geoname_id,
                                label: geonameData?.data.name,
                                code: geonameData?.data.country
                            }
                        };
                    });
                }
            }
        })();
    }, [localization, lang]);

    const currencyList = useMemo(() => {
        if (currencies?.data?.length) {
            const userCurrency = currencies.data.find((el) => el.code === localization?.currency);
            const defaultCurrency = currencies.data.find((el) => el.main === true);
            if (userCurrency || defaultCurrency) {
                setFormData((prev) => ({
                    ...prev,
                    currency: userCurrency
                        ? {
                              value: userCurrency.code,
                              label:
                                  settings?.currency_format == 0 || settings?.currency_format == 1
                                      ? userCurrency.symbol
                                      : userCurrency.code
                          }
                        : {
                              value: defaultCurrency.code,
                              label:
                                  settings?.currency_format == 0 || settings?.currency_format == 1
                                      ? defaultCurrency.symbol
                                      : defaultCurrency.code
                          }
                }));
            }
            return currencies.data.map((item) => ({
                id: item.code,
                title: settings?.currency_format == 0 || settings?.currency_format == 1 ? item.code : item.code
            }));
        }
        return [];
    }, [currencies?.data, localization?.currency, settings?.currency_format]);

    const setFormDataMain = useCallback(
        (key, val) => {
            if (key === 'country' && val) {
                let languageData =
                    activeLangs?.find((el) => el.alias === val.language) || activeLangs?.find((el) => el.isDefault);
                let language = languageData
                    ? {
                          language: {
                              value: languageData.alias,
                              label: languageData.name
                          }
                      }
                    : {};
                let currencyData =
                    currencyList?.find((el) => el.id === val.currency) || currencyList.find((el) => el.main === true);
                let currency = currencyData
                    ? {
                          currency: {
                              value: currencyData.id,
                              label: currencyData.id
                          }
                      }
                    : {};
                setFormData((pre) => ({
                    ...pre,
                    [key]: val,
                    ...language,
                    ...currency
                }));
            } else if (key !== 'city' && key !== 'region') {
                setFormData((pre) => ({
                    ...pre,
                    [key]: val
                }));
            }
        },
        [activeLangs, currencyList]
    );
    const formDataFields = useMemo(
        () => getFormData(currencyList, langsForMenu, formData, trans, isStorePage, userToken, display_country),
        [formData, trans, langsForMenu, currencyList, isStorePage, userToken, display_country]
    );

    const sender = async (formData, setAllowedSend, setFieldsErr) => {
        setFieldsErr({});
        if (Object.keys(formData).length && Object.keys(formData).every((key) => formData[key])) {
            let newLocalization = Object.keys(formData).reduce(
                (acc, key) => ({
                    ...acc,
                    [key]:
                        key === 'country'
                            ? {
                                  id: formData[key]?.id || formData[key]?.geoname_id || formData[key]?.value,
                                  code: formData[key]?.code || formData[key]?.country
                              }
                            : formData[key]?.value
                }),
                {}
            );

            // країна відображення
            const display_country = formData?.display_country;
            setState('display_country', display_country);
            zendoStorage.set('display_country', display_country);
            const reload_pages = ['/[lang]/reports/structure', '/[lang]/treegraph/[...structure]'];
            if (reload_pages.includes(router.pathname))
                setTimeout(() => {
                    window.location.reload();
                }, 0);

            if (!!newLocalization && typeof newLocalization === 'object') {
                setState('localization', newLocalization);
            }
            if (lang !== newLocalization.language) {
                setState('lang', newLocalization.language);
            }
            setIsShowPortalModal(false);
            setIsShowConfirmModal && setIsShowConfirmModal(false);
        } else if (Object.keys(formData).length) {
            setFieldsErr(
                Object.keys(formData).reduce((acc, key) => {
                    if (formData[key]) return acc;
                    return {
                        ...acc,
                        [key]: [
                            <T
                                key='SiteSettings-app-setting-error'
                                customTranslates={trans}
                                textName='SiteSettings-app-setting-error'
                                defaultStr='Поле обязательно для заполнения'
                                page='widget'
                                group='SiteSettings'
                            />
                        ]
                    };
                }, {})
            );
            setAllowedSend(true);
        }
    };
    return (
        <FormComponent
            data={formDataFields}
            setFormDataMain={setFormDataMain}
            formDataMain={formData}
            sendData={{ sender }}
            formStyles={{
                display_country: {
                    wrapper: css.displayCountryWrapper
                }
            }}
            buttons={[
                {
                    type: 'cancel',
                    clickFunc: () => cancel()
                },
                {
                    type: 'ok',
                    buttonText: <T textName='save' defaultStr='Сохранить' page='all' />
                }
            ]}
            customData={{}}
        />
    );
};
const RegionAndCurrencySwitcher = ({ setIsShowConfirmModal, setIsShowPortalModal, mobile, isStorePage }) => {
    const { translation } = useContext(LangContext);
    const { localization, lang } = useContext(UserContext);
    const [trans, setTranslates] = useState(null);
    const loadData = useCallback(async () => {
        const trans = await getWidgetTranslates(lang, 'SiteSettings', translation, setTranslates);
        const geonameData = await getGeoName(localization.country?.id || localization.country?.value, lang);
        return {
            trans,
            geoname: geonameData?.data
        };
    }, [lang, localization.country?.id, localization.country?.value, translation]);
    const cancel = () => {
        setIsShowPortalModal(false);
        setIsShowConfirmModal && setIsShowConfirmModal(false);
    };
    return (
        <Portal
            fullWidth={mobile}
            fullHeight={mobile}
            closeHandler={() => {
                setIsShowPortalModal(false);
            }}
        >
            <div className={mobile ? css.modalWrapperMobile : null} onClick={() => setIsShowPortalModal(false)}>
                <div
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                    className={mobile ? css.modalBody : css.modalWrapper}
                >
                    {mobile ? (
                        <div className={css.modalTopDecor} />
                    ) : (
                        <div onClick={() => cancel()} className={css.modalClose}>
                            {svgSprite('cross', {
                                className: css.modalCloseSvg,
                                fill: 'var(--greyExtraDarkColor)'
                            })}
                        </div>
                    )}
                    <p className={css.modalTitle}>
                        <T
                            customTranslates={trans}
                            textName='SiteSettings-app-setting'
                            defaultStr='Настройки сайта'
                            page='widget'
                            group='SiteSettings'
                        />
                    </p>
                    <Skeleton
                        elements={['blockMD', 'empty', 'blockMD', 'empty', 'blockMD', 'empty', 'blockMD']}
                        handler={loadData}
                        Component={RenderForm}
                        componentProps={{
                            setIsShowPortalModal,
                            setIsShowConfirmModal,
                            cancel,
                            isStorePage
                        }}
                    />
                </div>
            </div>
        </Portal>
    );
};
export default RegionAndCurrencySwitcher;
